/**
 * iOS platform detection + the install-banner dismissal store.
 *
 * Lives here rather than inside IosInstallBanner because two very
 * different surfaces need the same answers: the banner, and Profile →
 * Notifications. Web push on iOS is only available to a PWA installed to
 * the Home Screen (iOS 16.4+) — in a Safari tab `window.Notification` does
 * not exist at all, so there is no permission to grant and no token to
 * register. Anything that offers to "enable notifications" has to know
 * that, or it silently offers nothing.
 */
const STORAGE_KEY = 'mycity.ios-install';

/** Any iPhone/iPad/iPod, including iPadOS masquerading as a Mac. */
export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes('Macintosh') && typeof document !== 'undefined' && 'ontouchend' in document)
  );
}

/**
 * Facebook / Instagram / Line in-app browsers. They render with WebKit but
 * expose no share sheet that can add to the Home Screen, so a PWA can
 * never be installed from one — the user has to open the link in Safari
 * itself first.
 */
export function isIosInAppBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  return isIos() && /FBAN|FBAV|Instagram|Line\//.test(navigator.userAgent);
}

/** iOS, in a browser that CAN install a PWA (i.e. not an in-app browser). */
export function isIosSafariLike(): boolean {
  return isIos() && !isIosInAppBrowser();
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS: `navigator.standalone === true` when launched from Home Screen.
  // Android / desktop: matchMedia('(display-mode: standalone)').
  const navLike = navigator as Navigator & { standalone?: boolean };
  if (navLike.standalone === true) return true;
  try {
    return window.matchMedia('(display-mode: standalone)').matches;
  } catch {
    return false;
  }
}

/**
 * True when push cannot work on this device until the app is installed to
 * the Home Screen — the only honest explanation for "Enable notifications
 * isn't here".
 */
export function needsHomeScreenForPush(): boolean {
  return isIos() && !isStandalone();
}

export function isInstallDismissed(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'dismissed';
  } catch {
    return true;
  }
}

export function dismissInstallBanner(): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, 'dismissed');
  } catch {
    /* noop — a private-mode failure must not break the dismiss click */
  }
  bump();
}

/**
 * Bring the install banner back after a dismissal. Dismissal is otherwise
 * permanent, which left a citizen who had tapped X with no route to the
 * instructions at all — this is that route, from the place they go looking
 * for notification settings.
 */
export function showInstallBanner(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
  bump();
}

let version = 0;
const listeners = new Set<() => void>();

function bump(): void {
  version += 1;
  for (const l of listeners) l();
}

export function subscribeInstallBanner(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function installBannerVersion(): number {
  return version;
}

export function installBannerServerVersion(): number {
  return 0;
}
